import { useCallback, useState } from "react";
import { GameBoard } from "./components/game-board";
import { Lobby } from "./components/lobby";
import { RulesSheet } from "./components/rules-sheet";
import { Toast } from "./components/toast";
import { WaitingRoom } from "./components/waiting-room";
import { sound } from "./feedback";
import { useGame } from "./use-game";

interface Preferences {
  sound: boolean;
  haptics: boolean;
}

function readPreferences(): Preferences {
  try {
    const stored = JSON.parse(localStorage.getItem("chin:preferences:v1") ?? "null") as Partial<Preferences> | null;
    return {
      sound: stored?.sound ?? true,
      haptics: stored?.haptics ?? true,
    };
  } catch {
    return { sound: true, haptics: true };
  }
}

function savePreferences(preferences: Preferences): void {
  try {
    localStorage.setItem("chin:preferences:v1", JSON.stringify(preferences));
  } catch {
    // Preferences can remain in memory when storage is unavailable.
  }
}

export function App() {
  const game = useGame();
  const [rulesOpen, setRulesOpen] = useState(false);
  const [preferences, setPreferences] = useState(readPreferences);

  const closeRules = useCallback(() => setRulesOpen(false), []);

  function toggleSound(): void {
    setPreferences((current) => {
      const next = { ...current, sound: !current.sound };
      savePreferences(next);
      if (next.sound) sound("ready", true);
      return next;
    });
  }

  function toggleHaptics(): void {
    setPreferences((current) => {
      const next = { ...current, haptics: !current.haptics };
      savePreferences(next);
      return next;
    });
  }

  return (
    <>
      {!game.room ? (
        <Lobby
          connection={game.connection}
          busy={game.busy}
          lobby={game.lobby}
          seeking={game.seeking}
          onQuickMatch={game.quickMatch}
          onBotMatch={game.botMatch}
          onTakeSeek={game.takeSeek}
          onCancelSeek={game.cancelSeek}
          onOpenRules={() => setRulesOpen(true)}
        />
      ) : game.room.phase === "waiting" || game.room.phase === "countdown" ? (
        <WaitingRoom
          room={game.room}
          onReady={() => {
            sound("ready", preferences.sound);
            game.ready();
          }}
          onLeave={game.leave}
        />
      ) : (
        <GameBoard
          room={game.room}
          connection={game.connection}
          latency={game.latency}
          emotes={game.emotes}
          soundEnabled={preferences.sound}
          hapticsEnabled={preferences.haptics}
          onToggleSound={toggleSound}
          onOpenRules={() => setRulesOpen(true)}
          onPlay={game.play}
          onChin={game.chin}
          chinPending={game.chinPending}
          onRefresh={game.refresh}
          onReady={game.ready}
          onEmote={game.sendEmote}
          onLeave={game.leave}
        />
      )}

      <RulesSheet
        open={rulesOpen}
        hapticsEnabled={preferences.haptics}
        onToggleHaptics={toggleHaptics}
        onClose={closeRules}
      />
      {game.notice ? <Toast notice={game.notice} onDismiss={game.dismissNotice} /> : null}
    </>
  );
}
